"use client";

import { useEffect, useState } from "react";
import Link from "@/components/ui/LocaleLink";
import { useActionState } from "react";
import { useTranslations } from "next-intl";
import LastUsedTag, { useLastSignIn } from "@/components/auth/LastUsedTag";
import { Eye, EyeSlash } from "@phosphor-icons/react/dist/ssr";
import { loginAction } from "@/lib/actions/auth";
import { isValidEmail } from "@/lib/validations";
import { createClient } from "@/lib/supabase/client";
import LoginLinkForm from "./LoginLinkForm";
import { useNextParam } from "./useNextParam";
import { useTurnstile } from "./useTurnstile";
import { CAPTCHA_FIELD } from "@/lib/turnstile";
import { BusyLabel } from "@/components/ui/Spinner";

export default function LoginForm({
  next: nextProp,
  allowLoginLink = true,
}: {
  /** A fixed return path (/admin/login); otherwise `?next=` from the URL. */
  next?: string;
  /**
   * Admins are refused a login link server-side (gating the role that grants
   * Verified badges on inbox access is a downgrade), so offering the toggle on
   * /admin/login would be a dead end that answers "if that address has an
   * account, we've sent a link" and never sends one.
   */
  allowLoginLink?: boolean;
}) {
  const t = useTranslations("auth");
  const tErr = useTranslations("authErrors");
  const last = useLastSignIn();
  const urlNext = useNextParam();
  const next = nextProp ?? urlNext;
  const [state, action, pending] = useActionState(loginAction, undefined);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());
  // Both ways in stay available. Existing pros and every client sign in with
  // a password today, so the link is an addition, never a migration.
  const [mode, setMode] = useState<"password" | "link">("password");
  const linkMode = allowLoginLink && mode === "link";
  const captcha = useTurnstile("login");
  const [captchaMsg, setCaptchaMsg] = useState<string | null>(null);
  const { reset: resetCaptcha } = captcha;

  // Every answer from loginAction means the token was spent (a token is single
  // use), so the next attempt needs a fresh one. Success redirects away.
  useEffect(() => {
    if (state) resetCaptcha();
  }, [state, resetCaptcha]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    const form = e.currentTarget;
    const email    = (form.elements.namedItem("email")    as HTMLInputElement).value.trim();
    const password = (form.elements.namedItem("password") as HTMLInputElement).value;
    const fields: string[] = [];
    if (!email)    fields.push("email");
    if (!password) fields.push("password");
    if (fields.length) {
      e.preventDefault();
      setFieldErrors(new Set(fields));
      return;
    }
    setFieldErrors(new Set());
    const blocked = captcha.guard();
    if (blocked) {
      e.preventDefault();
      setCaptchaMsg(tErr(blocked));
      return;
    }
    setCaptchaMsg(null);
  }

  async function handleOAuth(provider: "google") {
    const supabase = createClient();
    // Preserve return-to so OAuth users land on the right page (e.g. the
    // /pro/dashboard a guard bounced them from). Our /auth/callback route
    // reads `?next=` and applies the same safe-redirect validation as the
    // email/password loginAction.
    const callback = `${window.location.origin}/auth/callback`;
    await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: next ? `${callback}?next=${encodeURIComponent(next)}` : callback,
      },
    });
  }

  const errInput    = "rounded-control border border-error-line bg-red-50/40 px-13 py-8 text-sm text-navy placeholder:text-gray-500 transition-colors";
  const normalInput = "rounded-control border border-navy/20 bg-white px-13 py-8 text-sm text-navy placeholder:text-gray-500 focus-visible:border-gold focus-visible:outline-none";

  return (
    <>
      {/* ── Social auth ── */}
      <div className="mt-21 space-y-8">
        <button
          type="button"
          onClick={() => handleOAuth("google")}
          className={`relative flex w-full items-center justify-center gap-8 rounded-control border ${last === "google" ? "border-navy/55" : "border-navy/20"} py-[10px] text-sm font-semibold text-navy transition-colors hover:border-navy/50 hover:bg-navy/[0.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold`}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/icon_google.svg" alt="" width={16} height={16} aria-hidden="true" />
          {t("continueGoogle")}
          {last === "google" && <LastUsedTag placement="corner" />}
        </button>
      </div>

      {/* ── Divider ── */}
      <div className="relative my-21 flex items-center" aria-hidden="true">
        <div className="flex-1 border-t border-navy/10" />
        <span className="mx-13 text-xs uppercase tracking-[0.08em] text-gray-500">{t("or")}</span>
        <div className="flex-1 border-t border-navy/10" />
      </div>

      {/* ── Email / password form ── */}
      {!linkMode ? (
      <form action={action} onSubmit={handleSubmit} className="space-y-13" noValidate aria-label={t("signInFormAria")}>
      {next && <input type="hidden" name="next" value={next} />}
      {captcha.token && <input type="hidden" name={CAPTCHA_FIELD} value={captcha.token} />}
      {(captchaMsg ?? state?.error) && (
        <p role="alert" className="rounded-control bg-red-50 px-13 py-8 text-sm text-red-700">
          {captchaMsg ?? state?.error}
        </p>
      )}

      <div className="flex flex-col gap-3">
        <label
          htmlFor="email"
          className="text-xs font-semibold uppercase tracking-wider text-navy"
        >
          {t("emailLabel")}
        </label>
        <input
          id="email"
          type="email"
          name="email"
          required
          aria-required="true"
          autoComplete="email"
          placeholder="you@example.com"
          className={`w-full ${fieldErrors.has("email") ? errInput : normalInput}`}
        onChange={() => setFieldErrors(prev => { const n = new Set(prev); n.delete("email"); return n; })}
        onBlur={(e) => { const v = e.target.value.trim(); if (v && !isValidEmail(v)) setFieldErrors(prev => new Set([...prev, "email"])); }}
        />
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <label
            htmlFor="password"
            className="text-xs font-semibold uppercase tracking-wider text-navy"
          >
            {t("passwordLabel")}
          </label>
          <Link
            // Preserve `next` so a user who came from a protected page,
            // clicks "Forgot password?", resets, then signs in returns
            // to their original destination instead of the role dashboard.
            href={next ? `/reset?next=${encodeURIComponent(next)}` : "/reset"}
            className="text-xs text-gray-600 underline underline-offset-2 hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            {t("forgotPassword")}
          </Link>
        </div>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            name="password"
            required
            aria-required="true"
            autoComplete="current-password"
            placeholder="••••••••"
            className={`w-full ${fieldErrors.has("password") ? errInput : normalInput} pr-[38px]`}
            onChange={() => setFieldErrors(prev => { const n = new Set(prev); n.delete("password"); return n; })}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? t("hidePassword") : t("showPassword")}
            className="absolute inset-y-0 right-0 flex items-center px-13 text-gray-500 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            {showPassword
              ? <EyeSlash size={16} aria-hidden="true" />
              : <Eye size={16} aria-hidden="true" />
            }
          </button>
        </div>
      </div>

      {captcha.widget}

      <button
        type="submit"
        disabled={pending}
        aria-disabled={pending}
        className="relative mt-8 w-full rounded-control bg-gold py-13 text-sm font-semibold uppercase tracking-wider text-navy transition-colors hover:bg-gold-hover disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
      >
        <BusyLabel busy={pending}>{pending ? t("signingIn") : t("signIn")}</BusyLabel>
        {last === "password" && <LastUsedTag placement="onGold" />}
      </button>
      </form>
      ) : (
        <LoginLinkForm lastUsed={last === "link"} />
      )}

      {/* ── Password ⇄ link toggle ── */}
      {allowLoginLink && (
      <p className="mt-13 text-center text-sm text-gray-600">
        <button
          type="button"
          onClick={() => setMode((m) => (m === "password" ? "link" : "password"))}
          className="whitespace-nowrap font-semibold text-navy underline underline-offset-2 hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
        >
          {mode === "password" ? t("linkToggle") : t("passwordToggle")}
        </button>
        {/* The tag follows the toggle when the method it switches TO was the last one used. */}
        {(mode === "password" ? last === "link" : last === "password") && <LastUsedTag placement="inline" />}
      </p>
      )}
    </>
  );
}
